import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { tasksAPI } from '../lib/api';
import { formatDate, getDaysUntil, getStatusColor, getStatusLabel, getPriorityColor, getPriorityLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  Plus, 
  Search,
  Filter,
  ClipboardList,
  MapPin,
  Calendar,
  Image as ImageIcon,
  Milestone,
  Loader2,
  User
} from 'lucide-react';
import Layout from '../components/Layout';
import { TranslatableSpan } from '../components/TranslatableText';

const TaskListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const statusFilter = searchParams.get('status') || 'all';
  const priorityFilter = searchParams.get('priority') || 'all';
  const hasMilestonesFilter = searchParams.get('has_milestones') === 'true';
  const overdueFilter = searchParams.get('overdue') === 'true';
  const categoryFilter = searchParams.get('category') || '';
  const assigneeFilter = searchParams.get('assignee') || '';
  const unassignedFilter = searchParams.get('unassigned') === 'true';

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const params = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (priorityFilter !== 'all') params.priority = priorityFilter;
        
        const response = await tasksAPI.getAll(params);
        setTasks(response.data);
      } catch (err) {
        console.error('Failed to fetch tasks:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTasks();
  }, [statusFilter, priorityFilter]);

  // Apply client-side filters
  const filteredTasks = tasks.filter(task => {
    // Search filter
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.location?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Milestones filter - show tasks with incomplete milestones
    if (hasMilestonesFilter) {
      const hasIncompleteMilestones = task.milestones?.some(m => !m.completed);
      if (!hasIncompleteMilestones) return false;
    }
    
    // Overdue filter
    if (overdueFilter) {
      const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'completed';
      if (!isOverdue) return false;
    }
    
    // Category filter
    if (categoryFilter) {
      if (task.category_name !== categoryFilter) return false;
    }
    
    // Assignee filter
    if (assigneeFilter) {
      const taskAssignee = task.assigned_to_name || 'Unassigned';
      if (taskAssignee !== assigneeFilter) return false;
    }
    
    // Unassigned filter
    if (unassignedFilter) {
      if (task.assigned_to) return false;
    }
    
    return matchesSearch;
  });

  // Sort tasks: Reported/Assigned first, then by newest, then by priority
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // First: Prioritize active work orders (reported, assigned) over completed
    // Reported and Assigned go to TOP, In Progress next, Completed at bottom
    const statusOrder = { 'reported': 4, 'assigned': 4, 'in_progress': 3, 'completed': 0 };
    const statusDiff = (statusOrder[b.status] || 0) - (statusOrder[a.status] || 0);
    if (statusDiff !== 0) return statusDiff;
    
    // Within same status priority, sort by creation date (newest first)
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    const dateDiff = dateB - dateA;
    
    // If created on different days, sort by date
    const dayA = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
    const dayB = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());
    if (dayA.getTime() !== dayB.getTime()) {
      return dateDiff;
    }
    
    // Within the same day, prioritize by milestone progress
    const getMilestoneScore = (task) => {
      if (!task.milestones || task.milestones.length === 0) return 0;
      const completed = task.milestones.filter(m => m.completed).length;
      const total = task.milestones.length;
      if (completed > 0 && completed < total) return 3; // In progress
      if (completed === 0 && total > 0) return 2; // Has milestones but none done
      if (completed === total) return 1; // All done
      return 0;
    };
    
    const scoreA = getMilestoneScore(a);
    const scoreB = getMilestoneScore(b);
    if (scoreB !== scoreA) return scoreB - scoreA;
    
    // Then by priority (high > medium > low)
    const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
    const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
    if (priorityDiff !== 0) return priorityDiff;
    
    // Finally by exact creation time (newest first)
    return dateDiff;
  });

  const handleStatusChange = (value) => {
    if (value === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };

  const handlePriorityChange = (value) => {
    if (value === 'all') {
      searchParams.delete('priority');
    } else {
      searchParams.set('priority', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="task-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Work Orders
            </h1>
            <p className="text-slate-400 mt-1">{sortedTasks.length} work order{sortedTasks.length !== 1 ? 's' : ''}</p>
          </div>
          <Button 
            onClick={() => navigate('/tasks/new')} 
            className="btn-primary flex items-center gap-2"
            data-testid="create-task-btn"
          >
            <Plus className="w-5 h-5" />
            New Work Order
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search work orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-industrial pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[140px] bg-slate-950 border-slate-700 text-white" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={handlePriorityChange}>
              <SelectTrigger className="w-[140px] bg-slate-950 border-slate-700 text-white" data-testid="priority-filter">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filter Badges */}
        {(hasMilestonesFilter || overdueFilter || categoryFilter || assigneeFilter || unassignedFilter) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-slate-400 text-sm">Active filters:</span>
            {hasMilestonesFilter && (
              <span 
                className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 border border-amber-500/50 rounded-sm text-xs font-mono uppercase cursor-pointer hover:bg-amber-500/30"
                onClick={() => {
                  searchParams.delete('has_milestones');
                  setSearchParams(searchParams);
                }}
              >
                Incomplete Milestones
                <span className="ml-1">×</span>
              </span>
            )}
            {overdueFilter && (
              <span 
                className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/50 rounded-sm text-xs font-mono uppercase cursor-pointer hover:bg-red-500/30"
                onClick={() => {
                  searchParams.delete('overdue');
                  setSearchParams(searchParams);
                }}
              >
                Overdue
                <span className="ml-1">×</span>
              </span>
            )}
            {categoryFilter && (
              <span 
                className="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-sm text-xs font-mono uppercase cursor-pointer hover:bg-purple-500/30"
                onClick={() => {
                  searchParams.delete('category');
                  setSearchParams(searchParams);
                }}
              >
                Category: {categoryFilter}
                <span className="ml-1">×</span>
              </span>
            )}
            {assigneeFilter && (
              <span 
                className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 rounded-sm text-xs font-mono uppercase cursor-pointer hover:bg-cyan-500/30"
                onClick={() => {
                  searchParams.delete('assignee');
                  setSearchParams(searchParams);
                }}
              >
                Assignee: {assigneeFilter}
                <span className="ml-1">×</span>
              </span>
            )}
            {unassignedFilter && (
              <span 
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-sm text-xs font-mono uppercase cursor-pointer hover:bg-blue-500/30"
                onClick={() => {
                  searchParams.delete('unassigned');
                  setSearchParams(searchParams);
                }}
              >
                Unassigned Only
                <span className="ml-1">×</span>
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tasks')}
              className="text-slate-400 hover:text-white text-xs"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Task List */}
        {loading ? (
          <div className="flex items-center justify-center h-[40vh]">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : sortedTasks.length > 0 ? (
          <div className="space-y-3">
            {sortedTasks.map((task) => {
              const daysUntil = getDaysUntil(task.deadline);
              const isOverdue = daysUntil !== null && daysUntil < 0 && task.status !== 'completed';
              const completedMilestones = task.milestones?.filter(m => m.completed).length || 0;
              const totalMilestones = task.milestones?.length || 0;
              
              return (
                <Link
                  key={task.id}
                  to={`/tasks/${task.id}`}
                  className="task-card block card-industrial hover:border-amber-500/50 transition-colors"
                  data-testid={`task-card-${task.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {task.work_order_number && (
                          <span className="text-cyan-400 font-mono text-sm bg-cyan-500/10 px-2 py-0.5 rounded">
                            {task.work_order_number}
                          </span>
                        )}
                        <span className={`priority-indicator ${task.priority}`}></span>
                        <h3 className="text-lg font-semibold text-white truncate">
                          <TranslatableSpan text={task.title} />
                        </h3>
                        <span className={`status-badge ${getStatusColor(task.status)}`}>
                          {getStatusLabel(task.status)}
                        </span>
                      </div>
                      
                      <p className="text-slate-400 text-sm line-clamp-1 mb-3">
                        <TranslatableSpan text={task.description} />
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                        {task.created_by_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {task.created_by_name}
                          </span>
                        )}
                        {task.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {task.location}
                          </span>
                        )}
                        {task.deadline && (
                          <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                            <Calendar className="w-4 h-4" />
                            {formatDate(task.deadline)}
                            {isOverdue && <span className="text-red-400 font-mono text-xs ml-1">(OVERDUE)</span>}
                          </span>
                        )}
                        {task.images?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-4 h-4" />
                            {task.images.length} photo{task.images.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {totalMilestones > 0 && (
                          <span className={`flex items-center gap-1 ${
                            completedMilestones > 0 && completedMilestones < totalMilestones 
                              ? 'text-amber-400 font-medium' 
                              : completedMilestones === totalMilestones 
                                ? 'text-green-400' 
                                : ''
                          }`}>
                            <Milestone className="w-4 h-4" />
                            {completedMilestones}/{totalMilestones}
                            {completedMilestones > 0 && completedMilestones < totalMilestones && (
                              <span className="text-xs bg-amber-500/20 px-1.5 py-0.5 rounded text-amber-400 ml-1">IN PROGRESS</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Priority Badge - Desktop */}
                    <div className="hidden md:flex items-center">
                      <span className={`px-3 py-1 rounded-sm text-xs font-mono uppercase ${getPriorityColor(task.priority)} border`}>
                        {getPriorityLabel(task.priority)}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="empty-state py-16">
            <ClipboardList className="w-16 h-16 text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Work Orders Found</h3>
            <p className="text-slate-400 mb-6">
              {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Create your first work order to get started'}
            </p>
            <Button 
              onClick={() => navigate('/tasks/new')} 
              className="btn-primary"
              data-testid="empty-create-task-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Work Order
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TaskListPage;
